import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import "../Loading.css";
import { PDFDownloadLink, PDFViewer, pdf } from "@react-pdf/renderer";
import AppointmentPDF from "./AppointmentPDF";

interface Medicine {
  itemName: string;
  doseText: string;
  doseValue: number;
  forDays: number;
}
interface LabItem {
  serviceName: string;
}
interface AppointmentDetail {
  CheifComplain: string;
  ProvisionalDiagnosis: string;
  TreatmentPlan: string;
  Medicines: Medicine[];
  LabItems: LabItem[];
}
interface DoctorInfo {
  doctorName: string;
  specializationName: string;
  appointmentDate: string;
  slotStartTime: string;
}
interface PatientInfo {
  patientName: string;
  dob: string;
  gender: string;
  address: string;
  phoneNo: string;
  bloodGroup: string;
}
interface AppointmentDetailPopupProps {
  appointmentId: string;
  onClose: () => void;
  doctorInfo: DoctorInfo;
  patientInfo: PatientInfo;
  followUpDate:string
  // location:string
}

const AppointmentDetailPopup: React.FC<AppointmentDetailPopupProps> = ({
  appointmentId,
  onClose,
  doctorInfo,
  patientInfo,
  followUpDate,
  // location
}) => {
  const [appointmentDetail, setAppointmentDetail] =
    useState<AppointmentDetail | null>(null);
  const [isMobile, setIsMobile] = useState(false);

  const getAppointmentDetails = useCallback(async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/Patient/GetAppointmentDetails`,
        { params: { appointmentId } }
      );
      const detailArray = response.data;
      setAppointmentDetail(Array.isArray(detailArray) ? detailArray[0] : null);
    } catch (error) {
      console.error("Error fetching appointment details:", error);
      setAppointmentDetail(null);
    }
  }, [appointmentId]);

  useEffect(() => {
    getAppointmentDetails();
  }, [getAppointmentDetails]);

  // detect screen size
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 498);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

 const formatDate = (dateStr: string) => {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
      year: "numeric",
    timeZone: "Asia/Kolkata",
  });
};

const formatTime = (dateStr: string, timeStr: string) => {
  if (!dateStr || !timeStr) return "";
  const [hour, minute] = timeStr.split(":");
  const date = new Date(dateStr);
  date.setHours(parseInt(hour), parseInt(minute));
  return date.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    timeZone: "Asia/Kolkata",
  });
};

const getDayName = (dateStr: string) => {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("en-IN", {
    weekday: "long",
    timeZone: "Asia/Kolkata",
  });
};

  if (!appointmentDetail) {
    return (
      <div className="relative w-full h-[200px] flex justify-center items-center py-10 bg-white ">
        <div className="absolute top-2/3 left-1/2 transform -translate-x-1/2 -translate-y-1/2 loader"></div>
      </div>
    );
  }
  return (
  <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4">
    {/* Mobile View */}
    <div className="block md:hidden relative bg-white rounded-2xl shadow-2xl w-full max-w-sm max-h-[80vh] overflow-y-auto">
      
      {/* Mobile Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-100 bg-gradient-to-r from-teal-50 to-teal-100">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl flex items-center justify-center shadow-lg">
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900">E-Prescription</h3>
            <p className="text-xs text-gray-600">Medical Details</p>
          </div>
        </div>
        
        {/* Mobile Close Button */}
        <button
  onClick={onClose}  // ✅ Changed from placeholder comment
          className="w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-all duration-200"
        >
          <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Mobile Prescription Details */}
      <div className="p-6 space-y-6">
        {/* Patient Information */}
        <div className="bg-gray-50 rounded-xl p-4">
          <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
            <svg className="w-5 h-5 mr-2 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            Patient Details
          </h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Name:</span>
              <span className="font-medium text-gray-900">{patientInfo.patientName}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Phone:</span>
              <span className="font-medium text-gray-900">{patientInfo.phoneNo}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Gender:</span>
              <span className="font-medium text-gray-900">{patientInfo.gender}</span>
            </div>
          </div>
        </div>

        {/* Appointment Information */}
        <div className="bg-gray-50 rounded-xl p-4">
          <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
            <svg className="w-5 h-5 mr-2 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3a2 2 0 012-2h4a2 2 0 012 2v4m-6 9l6 6m0 0l6-6m-6 6V9a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2h6z" />
            </svg>
            Appointment Info
          </h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Doctor:</span>
              <span className="font-medium text-gray-900">{doctorInfo.doctorName}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Date:</span>
              <span className="font-medium text-gray-900">{formatDate(doctorInfo.appointmentDate)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Time:</span>
              <span className="font-medium text-gray-900">{formatTime(doctorInfo.appointmentDate,doctorInfo.slotStartTime)}</span>
            </div>
            <div className="flex justify-between">
          <span className="text-gray-600">Follow Up Date </span>
          <span className="font-medium text-gray-900">{formatDate(followUpDate)}</span>
        </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Department:</span>
              <span className="font-medium text-gray-900">{doctorInfo.specializationName}</span>
            </div>
          </div>
        </div>

        {/* Chief Complaints */}
        <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
          <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
            <svg className="w-5 h-5 mr-2 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Chief Complaints
          </h4>
          <div className="text-sm">
            {appointmentDetail.CheifComplain ? (
              appointmentDetail.CheifComplain.split(",").map((item, idx) => (
                <div key={idx} className="flex items-start mb-1">
                  <span className="text-teal-600 mr-2">•</span>
                  <span className="text-gray-700">{item.trim()}</span>
                </div>
              ))
            ) : (
              <span className="text-gray-500 italic">None provided</span>
            )}
          </div>
        </div>

        {/* Diagnosis */}
        <div className="bg-yellow-50 rounded-xl p-4 border border-yellow-100">
          <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
            <svg className="w-5 h-5 mr-2 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            Diagnosis
          </h4>
          <div className="text-sm">
            {appointmentDetail.ProvisionalDiagnosis ? (
              appointmentDetail.ProvisionalDiagnosis.split(",").map((item, idx) => (
                <div key={idx} className="flex items-start mb-1">
                  <span className="text-teal-600 mr-2">•</span>
                  <span className="text-gray-700">{item.trim()}</span>
                </div>
              ))
            ) : (
              <span className="text-gray-500 italic">No diagnosis provided</span>
            )}
          </div>
        </div>

        {/* Medicines */}
        {appointmentDetail.Medicines && appointmentDetail.Medicines.length > 0 && (
          <div className="bg-green-50 rounded-xl p-4 border border-green-100">
            <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
              <svg className="w-5 h-5 mr-2 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
              </svg>
              Prescribed Medicines
            </h4>
            <div className="space-y-3">
              {appointmentDetail.Medicines.map((medicine, idx) => (
                <div key={idx} className="bg-white rounded-lg p-3 border border-green-200">
                  <div className="flex justify-between items-start mb-2">
                    <span className="font-medium text-gray-900 text-sm">{idx + 1}. {medicine.itemName}</span>
                    <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">{medicine.forDays} days</span>
                  </div>
                  <div className="text-xs text-gray-600">
                    <span className="mr-4">Dosage: {medicine.doseText}</span>
                    <span>Details: {medicine.doseValue}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Lab Tests */}
        {appointmentDetail.LabItems && appointmentDetail.LabItems.length > 0 && (
          <div className="bg-purple-50 rounded-xl p-4 border border-purple-100">
            <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
              <svg className="w-5 h-5 mr-2 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
              </svg>
              Lab Investigations
            </h4>
            <div className="text-sm">
              {appointmentDetail.LabItems.map((lab, idx) => (
                <div key={idx} className="flex items-start mb-1">
                  <span className="text-teal-600 mr-2">•</span>
                  <span className="text-gray-700">{lab.serviceName}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Mobile Download Button */}
      <div className="p-6 pt-0">
        <PDFDownloadLink
          document={
            <AppointmentPDF
                  appointmentId={appointmentId}
                doctorInfo={doctorInfo}
                patientInfo={patientInfo}
                appointmentDetail={appointmentDetail}
                followUpDate={followUpDate}
              />
          }
          fileName={`${patientInfo.patientName}_EPrescription.pdf`}
        >
          {({ loading }) => (
            <button className="w-full py-4 bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white rounded-xl font-semibold text-base transition-all duration-200 shadow-lg active:scale-95 flex items-center justify-center space-x-2">
              {loading ? (
                <>
                  <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                  <span>Preparing...</span>
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span>Download Prescription</span>
                </>
              )}
            </button>
          )}
        </PDFDownloadLink>
      </div>

      {/* Mobile Footer */}
      <div className="px-6 pb-6">
        <div className="flex items-center justify-center space-x-2 text-xs text-gray-500 bg-gray-50 rounded-lg p-3">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>Download PDF for complete prescription details</span>
        </div>
      </div>
    </div>

    {/* Tablet View */}
  {/* Tablet View - Mobile-like Layout */}
<div className="hidden md:block lg:hidden relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-y-auto">
  
  {/* Tablet Header */}
  <div className="flex items-center justify-between p-6 border-b border-gray-100 bg-gradient-to-r from-teal-50 to-teal-100">
    <div className="flex items-center space-x-4">
      <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl flex items-center justify-center shadow-lg">
        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      </div>
      <div>
        <h3 className="text-xl font-bold text-gray-900">E-Prescription</h3>
        <p className="text-sm text-gray-600">Medical Details</p>
      </div>
    </div>
    
    {/* Tablet Close Button */}
    <button
      onClick={onClose}  // ✅ Changed from placeholder comment
      className="w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-xl flex items-center justify-center transition-all duration-200 hover:shadow-md"
    >
      <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>
  </div>

  {/* Tablet Prescription Details - Same structure as mobile */}
  <div className="p-8 space-y-6">
    {/* Patient Information */}
    <div className="bg-gray-50 rounded-xl p-6">
      <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
        <svg className="w-5 h-5 mr-2 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
        Patient Details
      </h4>
      <div className="space-y-3 text-base">
        <div className="flex justify-between">
          <span className="text-gray-600">Name:</span>
          <span className="font-medium text-gray-900">{patientInfo.patientName}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">Phone:</span>
          <span className="font-medium text-gray-900">{patientInfo.phoneNo}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">Gender:</span>
          <span className="font-medium text-gray-900">{patientInfo.gender}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">ID:</span>
          <span className="font-medium text-gray-900">{appointmentId}</span>
        </div>
      </div>
    </div>

    {/* Appointment Information */}
    <div className="bg-gray-50 rounded-xl p-6">
      <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
        <svg className="w-5 h-5 mr-2 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3a2 2 0 012-2h4a2 2 0 012 2v4m-6 9l6 6m0 0l6-6m-6 6V9a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2h6z" />
        </svg>
        Appointment Info
      </h4>
      <div className="space-y-3 text-base">
        <div className="flex justify-between">
          <span className="text-gray-600">Doctor:</span>
          <span className="font-medium text-gray-900">{doctorInfo.doctorName}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">Date:</span>
          <span className="font-medium text-gray-900">{formatDate(doctorInfo.appointmentDate)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">Time:</span>
          <span className="font-medium text-gray-900">{formatTime(doctorInfo.appointmentDate,doctorInfo.slotStartTime)}</span>
        </div>
 <div className="flex justify-between">
          <span className="text-gray-600">Follow Up Date </span>
          <span className="font-medium text-gray-900">{formatDate(followUpDate)}</span>
        </div>

        
        <div className="flex justify-between">
          <span className="text-gray-600">Department:</span>
          <span className="font-medium text-gray-900">{doctorInfo.specializationName}</span>
        </div>
      </div>
    </div>

    {/* Treatment Plan */}
    <div className="bg-gray-50 rounded-xl p-6">
      <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
        <svg className="w-5 h-5 mr-2 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m6 5a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
        Treatment Plan
      </h4>
      <div className="text-base">
        {appointmentDetail.TreatmentPlan ? (
          appointmentDetail.TreatmentPlan.split(",").map((item, idx) => (
            <div key={idx} className="flex items-start mb-2">
              <span className="text-teal-600 mr-2">•</span>
              <span className="text-gray-700">{item.trim()}</span>
            </div>
          ))
        ) : (
          <span className="text-gray-500 italic">No treatment plan provided</span>
        )}
      </div>
    </div>

    {/* Chief Complaints */}
    <div className="bg-blue-50 rounded-xl p-6 border border-blue-100">
      <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
        <svg className="w-5 h-5 mr-2 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        Chief Complaints
      </h4>
      <div className="text-base">
        {appointmentDetail.CheifComplain ? (
          appointmentDetail.CheifComplain.split(",").map((item, idx) => (
            <div key={idx} className="flex items-start mb-2">
              <span className="text-teal-600 mr-2">•</span>
              <span className="text-gray-700">{item.trim()}</span>
            </div>
          ))
        ) : (
          <span className="text-gray-500 italic">None provided</span>
        )}
      </div>
    </div>

    {/* Diagnosis */}
    <div className="bg-yellow-50 rounded-xl p-6 border border-yellow-100">
      <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
        <svg className="w-5 h-5 mr-2 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
        Diagnosis
      </h4>
      <div className="text-base">
        {appointmentDetail.ProvisionalDiagnosis ? (
          appointmentDetail.ProvisionalDiagnosis.split(",").map((item, idx) => (
            <div key={idx} className="flex items-start mb-2">
              <span className="text-teal-600 mr-2">•</span>
              <span className="text-gray-700">{item.trim()}</span>
            </div>
          ))
        ) : (
          <span className="text-gray-500 italic">No diagnosis provided</span>
        )}
      </div>
    </div>

    {/* Medicines - Same as mobile layout */}
    {appointmentDetail.Medicines && appointmentDetail.Medicines.length > 0 && (
      <div className="bg-green-50 rounded-xl p-6 border border-green-100">
        <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
          <svg className="w-5 h-5 mr-2 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
          </svg>
          Prescribed Medicines
        </h4>
        <div className="space-y-4">
          {appointmentDetail.Medicines.map((medicine, idx) => (
            <div key={idx} className="bg-white rounded-lg p-4 border border-green-200">
              <div className="flex justify-between items-start mb-3">
                <span className="font-medium text-gray-900 text-base">{idx + 1}. {medicine.itemName}</span>
                <span className="text-sm bg-green-100 text-green-800 px-3 py-1 rounded">{medicine.forDays} days</span>
              </div>
              <div className="text-sm text-gray-600">
                <span className="mr-6">Dosage: {medicine.doseText}</span>
                <span>Details: {medicine.doseValue}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    )}

    {/* Lab Tests - Same as mobile layout */}
    {appointmentDetail.LabItems && appointmentDetail.LabItems.length > 0 && (
      <div className="bg-purple-50 rounded-xl p-6 border border-purple-100">
        <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
          <svg className="w-5 h-5 mr-2 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
          </svg>
          Lab Investigations
        </h4>
        <div className="text-base">
          {appointmentDetail.LabItems.map((lab, idx) => (
            <div key={idx} className="flex items-start mb-2">
              <span className="text-teal-600 mr-2">•</span>
              <span className="text-gray-700">{lab.serviceName}</span>
            </div>
          ))}
        </div>
      </div>
    )}
  </div>

  {/* Tablet Download Button */}
  <div className="p-8 pt-0">
    <PDFDownloadLink
      document={
        <AppointmentPDF
                  appointmentId={appointmentId}
                doctorInfo={doctorInfo}
                patientInfo={patientInfo}
                appointmentDetail={appointmentDetail}
                followUpDate={followUpDate}
              />
      }
      fileName={`${patientInfo.patientName}_EPrescription.pdf`}
    >
      {({ loading }) => (
        <button className="w-full py-4 bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white rounded-xl font-semibold text-base transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center space-x-2">
          {loading ? (
            <>
              <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
              <span>Preparing...</span>
            </>
          ) : (
            <>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span>Download Prescription</span>
            </>
          )}
        </button>
      )}
    </PDFDownloadLink>
  </div>

  {/* Tablet Footer */}
  <div className="px-8 pb-8">
    <div className="flex items-center justify-center space-x-2 text-sm text-gray-500 bg-gray-50 rounded-lg p-4">
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <span>Download PDF for complete prescription details</span>
    </div>
  </div>
</div>

    {/* Desktop View */}
    <div className="hidden lg:block relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[80vh] overflow-hidden">
      
      {/* Desktop Header */}
      <div className="flex items-center justify-between p-2 border-b border-gray-100 bg-gradient-to-r from-teal-50 to-teal-100">
        <div className="flex items-center space-x-4">
          <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl flex items-center justify-center shadow-lg">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900">E-Prescription</h3>
            <p className="text-sm text-gray-600">Medical Prescription for {patientInfo.patientName}</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          {/* Desktop Download Button */}
          <PDFDownloadLink
            document={
              <AppointmentPDF
                  appointmentId={appointmentId}
                doctorInfo={doctorInfo}
                patientInfo={patientInfo}
                appointmentDetail={appointmentDetail}
                followUpDate={followUpDate}
              />
            }
            fileName={`${patientInfo.patientName}_EPrescription.pdf`}
          >
            {({ loading }) => (
              <button className="px-6 py-3 bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white rounded-xl font-semibold text-sm transition-all duration-200 shadow-lg hover:shadow-xl flex items-center space-x-2">
                {loading ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                    </svg>
                    <span>Preparing...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span>Download PDF</span>
                  </>
                )}
              </button>
            )}
          </PDFDownloadLink>
          
          {/* Desktop Close Button */}
          <button
  onClick={onClose}  // ✅ Changed from placeholder comment
            className="w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-xl flex items-center justify-center transition-all duration-200 hover:shadow-md"
          >
            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Desktop Content Area */}
      <div className="flex h-[calc(90vh-140px)]">
        
        {/* Right Side - PDF Viewer */}
        <div className="flex-1 bg-gray-50">
          <div className="w-full h-full p-4">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 h-full overflow-hidden">
              <PDFViewer className="w-full h-full border-0 rounded-xl">
                <AppointmentPDF
                  appointmentId={appointmentId}
                  doctorInfo={doctorInfo}
                  patientInfo={patientInfo}
                  appointmentDetail={appointmentDetail}
                  followUpDate={followUpDate}
                />
              </PDFViewer>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop Footer */}
      <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 text-sm text-gray-500">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>Prescription generated successfully • Follow medication instructions carefully</span>
          </div>
          <div className="text-xs text-gray-400">
            Generated on {new Date().toLocaleDateString()}
          </div>
        </div>
      </div>
    </div>
  </div>
  );
};

export default AppointmentDetailPopup;
